/**
 * REM-MOB-03 — minimum-supported-version gate. On a breaking backend change, old clients otherwise
 * fail loud (zod parse errors) with no graceful "please update." This is the pure decision the
 * force-update screen keys off: given the running app version and a backend-provided minimum, decide
 * whether the app MUST update before continuing.
 *
 * The backend supplies `minSupportedVersion` (e.g. via /health or a config endpoint); the running
 * version comes from `expo-application` / app.json. This module holds only the comparison so it is
 * unit-testable without any device/native surface.
 */

/** Parse a dotted version string ("1.2.3") into numeric segments (missing/garbage → 0). */
function parseVersion(v: string): number[] {
  return v
    .trim()
    .split('.')
    .map((x) => {
      const n = Number.parseInt(x, 10);
      return Number.isFinite(n) ? n : 0;
    });
}

/**
 * True when `current` is strictly BELOW `minRequired` (a force-update is required). A null/empty
 * `minRequired` means the backend imposes no minimum → never gate. Equal or newer → up to date.
 */
export function isUpdateRequired(current: string, minRequired: string | null | undefined): boolean {
  if (!minRequired) return false;
  const c = parseVersion(current);
  const m = parseVersion(minRequired);
  const len = Math.max(c.length, m.length);
  for (let i = 0; i < len; i += 1) {
    const cv = c[i] ?? 0;
    const mv = m[i] ?? 0;
    if (cv < mv) return true;
    if (cv > mv) return false;
  }
  return false; // equal → up to date
}
